/**
 * Structural Event Tracking — delegated listeners for common interactions.
 *
 * All listeners use event delegation on document so they survive
 * Swup content swaps without re-binding.
 */

import { pushEvent, pushPageView } from './datalayer.js';

/**
 * Collapse internal + surrounding whitespace so labels read cleanly in
 * analytics reports — element textContent often spans multiple lines/nodes
 * (e.g. a phone link wrapping an icon + label), which would otherwise land in
 * the dataLayer with newlines and runs of spaces.
 *
 * @param {string} [text]
 * @returns {string}
 */
function cleanLabel(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
}

/**
 * CTA click tracking.
 * Fires cta_click on any element with [data-track-cta].
 */
function initCtaTracking() {
    document.addEventListener('click', (e) => {
        const target = e.target.closest('[data-track-cta]');
        if (!target) return;

        pushEvent('cta_click', {
            text: cleanLabel(target.textContent),
            href: target.href || target.closest('a')?.href || '',
        });
    });
}

/**
 * Outbound link tracking.
 * Fires outbound_click on any <a> pointing to a different hostname.
 *
 * @param {string[]} excludedDomains — Hostnames to exclude (e.g., subdomains)
 */
function initOutboundTracking(excludedDomains = []) {
    document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href]');
        if (!link) return;

        try {
            const url = new URL(link.href, location.origin);
            if (url.hostname === location.hostname) return;
            if (excludedDomains.includes(url.hostname)) return;

            pushEvent('outbound_click', {
                href: link.href,
                text: cleanLabel(link.textContent),
            });
        } catch {
            // Invalid URL — skip tracking
        }
    });
}

/**
 * Scroll depth tracking.
 * Fires scroll_depth at 25%, 50%, 75%, 100% milestones, once each per page.
 * Resets on Swup content:replace.
 *
 * @param {import('swup').default} swup — Swup instance for reset hook
 */
function initScrollTracking(swup) {
    const milestones = [25, 50, 75, 100];
    let fired = new Set();
    let ticking = false;

    function checkScroll() {
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollHeight <= 0) return;

        const percent = Math.round((window.scrollY / scrollHeight) * 100);

        for (const milestone of milestones) {
            if (percent >= milestone && !fired.has(milestone)) {
                fired.add(milestone);
                pushEvent('scroll_depth', { percent: milestone });
            }
        }

        ticking = false;
    }

    window.addEventListener('scroll', () => {
        if (!ticking) {
            ticking = true;
            requestAnimationFrame(checkScroll);
        }
    }, { passive: true });

    // Reset milestones on Swup navigation
    swup.hooks.on('content:replace', () => {
        fired = new Set();
    });
}

/**
 * Element visibility tracking.
 * Fires element_view when [data-track-view] elements enter the viewport (50%).
 * Re-observes after Swup content:replace.
 *
 * @param {import('swup').default} swup — Swup instance for re-observe hook
 */
function initVisibilityTracking(swup) {
    let observer;

    function observe() {
        if (observer) observer.disconnect();

        observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        const el = entry.target;
                        pushEvent('element_view', {
                            label: el.dataset.trackLabel || el.tagName.toLowerCase(),
                            tagName: el.tagName.toLowerCase(),
                        });
                        observer.unobserve(el);
                    }
                }
            },
            { threshold: 0.5 }
        );

        document.querySelectorAll('[data-track-view]').forEach((el) => {
            observer.observe(el);
        });
    }

    observe();

    // Re-observe on Swup navigation
    swup.hooks.on('content:replace', () => {
        observe();
    });
}

/**
 * Click-to-call tracking.
 * Fires tel_click on any tel: link — no per-link attribute needed.
 */
function initTelTracking() {
    document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href^="tel:"]');
        if (!link) return;

        pushEvent('tel_click', {
            number: link.getAttribute('href').replace(/^tel:/, ''),
            label: link.dataset.trackLabel || cleanLabel(link.textContent),
        });
    });
}

/**
 * Email-link tracking.
 * Fires mail_click on any mailto: link.
 */
function initMailTracking() {
    document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href^="mailto:"]');
        if (!link) return;

        pushEvent('mail_click', {
            email: link.getAttribute('href').replace(/^mailto:/, '').split('?')[0],
            label: link.dataset.trackLabel || cleanLabel(link.textContent),
        });
    });
}

/**
 * Download tracking.
 * Fires download_click (a GA4 file_download / pseudo-pageview source) on any
 * link with a download attribute or an explicit [data-track-download] hook.
 */
function initDownloadTracking() {
    document.addEventListener('click', (e) => {
        const link = e.target.closest('a[download], a[data-track-download]');
        if (!link) return;

        pushEvent('download_click', {
            file_url: link.href || '',
            file_name: link.dataset.trackLabel || link.getAttribute('download') || link.href?.split('/').pop() || '',
        });
    });
}

/**
 * Form-success conversion signal (fallback).
 * Enquiry forms normally redirect to a configured thank-you page, whose own
 * pageview is the conversion signal. This is the fallback for when a form shows
 * its inline success state instead (no thank-you page configured): it fires a
 * /thank-you virtual pageview so the GA4 conversion goal is never missed. Fires
 * once per success element.
 *
 * @param {import('swup').default} swup — Swup instance for the page:view hook
 */
function initFormSuccessConversion(swup) {
    function fire() {
        const success = document.querySelector('.mh-form__success[data-submission-created="true"]:not([data-conversion-fired])');
        if (!success) return;
        success.setAttribute('data-conversion-fired', '');
        pushPageView(`${location.origin}/thank-you/`, 'Thank You');
    }

    fire();
    swup.hooks.on('page:view', fire);
}

/**
 * Initialise all structural event tracking.
 *
 * @param {import('swup').default} swup — Swup router instance
 * @param {Object} [options={}]
 * @param {string[]} [options.excludedDomains=[]] — Hostnames to exclude from outbound tracking
 */
export function initEventTracking(swup, options = {}) {
    initCtaTracking();
    initOutboundTracking(options.excludedDomains || []);
    initScrollTracking(swup);
    initVisibilityTracking(swup);
    initTelTracking();
    initMailTracking();
    initDownloadTracking();
    initFormSuccessConversion(swup);
}
